import React, { isValidElement, useContext, useEffect, useState } from 'react'
import { Button, Col, Container, Row, Spinner } from 'react-bootstrap'
import { DateInput, MultiSelectDropdown } from '../../InputComponents'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { trackPromise, usePromiseTracker } from 'react-promise-tracker'
import { AxiosResponse } from 'axios'
import { AppUtil } from '../../../utils/app.utils'
import { getSampleNames } from '../../../services/QiagenManager'
import { Patient } from '../../../models/RegisterPatient'
import * as _ from 'lodash'
import { MASTER_DATA_KEYS as MDK} from '../../../utils/constants'
import { MasterDataContext } from '../../../contexts'

const translatn = 'qiagenManagerContainer.'
const parentClass = 'vcr-pp-qiagen-manager-'

type MultSelectSampleNameItem = {
    label: string
    value: string
}

type Value = number | string | null | undefined

type Details = {
    label: string
    value: Value
    show?: boolean
}

const renderRow = (rowArr: Details[], index: number) => {
    return (
        <div key={index}>
            <Row border="dark" key={index}>
                {rowArr.map((col) => (                  
                    <Col lg={2} md={2} sm={2} className={`${parentClass}`} key={col.label}>
                          <div className={`${parentClass}label`}>{col.label}</div>                         
                          { isValidElement(col.value) ? col.value :
                              <div className={`${parentClass}value`}>{col.value  ? col.value?.toString() : '-'}</div>
                          } 
                        </Col>
                ))}
            </Row>
        </div>
    )
}

export const QiagenManagerSearch = () => {

    const { t } = useTranslation()
    const formMethods = useFormContext()
    const { masterData } = useContext(MasterDataContext)
    

    const { watch, getValues } = formMethods
    const [sampleNameLists, setSampleNameLists] = useState<MultSelectSampleNameItem[]>([])
    const [patientDetails, setPatientDetails] = useState<Patient>()

    const { promiseInProgress } = usePromiseTracker({ area: 'get-search-details' })
    const { promiseInProgress: loadingSampleName } = usePromiseTracker({ area: 'get-sample-names' })

    const searchSamples = () => {
        trackPromise(
            getSampleNames({ sampleName: getValues('sampleName')?.[0]?.value ?? '' }).then(
                (res: AxiosResponse<Patient>) => {
                    setPatientDetails(res.data)
                },
                (error) => {
                    AppUtil.logError(error)
                },
            ), 'get-search-details'
        )
    }

    useEffect(() => {
        if (watch('sampleDate')) {
            setSampleNameLists([])
            setPatientDetails({} as Patient)
            formMethods.setValue('sampleName', [])
            trackPromise(
                getSampleNames({ sampleDate: AppUtil.formatDateForPayload(getValues('sampleDate') ?? '') ?? '' }).then(
                    (res: AxiosResponse<string[]>) => {
                        const sampleData: MultSelectSampleNameItem[] = res.data.map((item: string) => {
                            return {
                                label: item,
                                value: item
                            }
                        })
                        setSampleNameLists(sampleData)
                    },
                    (error) => {
                        AppUtil.logError(error)
                    },
                ), 'get-sample-names'
            )
        }
    }, [watch('sampleDate')])

    useEffect(() => {
        if(watch('sampleName')) {
            setPatientDetails({} as Patient)
        }
    }, [watch('sampleName')])

    const detailsArr = [
        {
            label: t(`${translatn}gender`),
            value: AppUtil.getFormattedValueWithOther(
                    MDK.GENDER,
                    masterData,
                    patientDetails?.gender,
                    patientDetails?.genderOther,
                  ),
            show: true,
        },
        {
            label: t(`${translatn}age`),
            value: patientDetails?.age,
            show: true,
        },
        {
            label: t(`${translatn}typeOfCancer`),
            value: AppUtil.getLabel(MDK.TYPE_OF_CANCER, masterData, patientDetails?.cancerType),
            show: true,
        },
        {
            label: t(`${translatn}diagnosis`),
            value: patientDetails?.diagnosis,
            show: true,
        },
    ]

    const data = detailsArr.filter((details) => details.show === true)

    const rowCount = _.chunk(data, 5)

    return <><Row className={`${parentClass}form-data-input`} >
        <Col>
            <DateInput
                name={`sampleDate`}
                id={'sampleDate'}
                title={t(`${translatn}sampleDate`)}
                placeholder={t(`${translatn}sampleNamePlaceholder`)}
                disabled={false}
                readOnly={false}
                max={new Date()}
                required={true}
            />
        </Col>
        <Col>
            <MultiSelectDropdown
                name={`sampleName`}
                id="sampleName"
                title={t(`${translatn}sampleName`)}
                placeholder={t(`${translatn}sampleNamePlaceholder`)}
                disabled={false}
                required={true}
                optionsValueArray={sampleNameLists}
                hideSelectedValues={false}
                singleSelect={true}
                loading={loadingSampleName}
            />
        </Col>
    </Row>
        <Row className='mt-4 mb-4'>
            <Col className={`d-flex justify-content-end`}>
                <Button className={`${parentClass}search-btn`} type="submit" onClick={formMethods.handleSubmit(searchSamples)}>
                    {t(`${translatn}search`)}{' '}
                </Button>
            </Col>
        </Row>

        {promiseInProgress ? <Row className={`mt-5`}><Col><Spinner className={`spinner`} /></Col></Row> :
            !_.isEmpty(patientDetails) && (
                <>
                    <hr />
                    <Container fluid>{rowCount.map((row, index) => renderRow(row, index))}</Container></>
            )
        }
    </>
}